import { useState } from 'react'
import {
  HiX,
  HiQuestionMarkCircle,
  HiLightBulb,
  HiBookOpen,
  HiChat,
  HiMail,
  HiExternalLink,
  HiPlay,
  HiShieldCheck,
  HiBell,
  HiClipboardList,
  HiCheck,
} from 'react-icons/hi'

const TABS = [
  { id: 'getting-started', label: 'Getting Started', icon: HiPlay },
  { id: 'tasks-boards', label: 'Tasks & Boards', icon: HiClipboardList },
  { id: 'ai-assistant', label: 'HAL 9000', icon: HiChat },
  { id: 'roles-permissions', label: 'Roles & Permissions', icon: HiShieldCheck },
  { id: 'notifications', label: 'Notifications', icon: HiBell },
  { id: 'faq', label: 'FAQ', icon: HiBookOpen },
]

const GETTING_STARTED_ITEMS = [
  { title: 'Create your first project', description: 'Click "New Project" on the dashboard to get started' },
  { title: 'Add team members', description: 'Go to project settings and invite colleagues by email' },
  { title: 'Create a Kanban board', description: 'Inside a project, create boards to organize tasks visually' },
  { title: 'Navigate the sidebar', description: 'Use the left sidebar to quickly switch between Dashboard, My Tasks, HAL 9000, Transcripts, and Settings. Projects and Departments are listed below.' },
  { title: 'Customize your experience', description: 'Visit Settings > Appearance to switch between Light, Dark, or System theme.' },
]

const TASKS_BOARDS_ITEMS = [
  { title: 'Create tasks', description: 'Use the "+" button on any column or ask HAL 9000' },
  { title: 'Set priorities', description: 'Mark tasks as Low, Medium, High, or Critical' },
  { title: 'Track progress', description: 'Drag tasks between columns to update status' },
  { title: 'Add due dates', description: 'Set deadlines to stay on schedule' },
  { title: 'Use the Calendar view', description: 'Switch to Calendar view on any board to see tasks plotted by due date. Click a date to create a task.' },
  { title: 'Task dependencies', description: 'Link tasks that block each other. Open a task and use the Dependencies section to add blockers.' },
  { title: 'Bulk operations', description: 'Use the Table view for quick inline editing of multiple tasks at once.' },
]

const AI_ASSISTANT_ITEMS = [
  { title: '"Create a task for..."', description: 'Ask the AI to create tasks for you' },
  { title: '"What\'s the status of..."', description: 'Get project or task summaries' },
  { title: '"Find all overdue tasks"', description: 'Search across your projects' },
  { title: '"Remind me to..."', description: 'Set up reminders for important items' },
  { title: '"Summarize this project"', description: 'Get an overview of project progress, overdue tasks, and key metrics.' },
  { title: 'Upload meeting notes', description: 'Upload transcript files and the AI will extract action items and create tasks automatically.' },
  { title: 'Context-aware chat', description: 'The AI has access to your projects, tasks, and team info. Ask it anything about your work.' },
]

const NOTIFICATIONS_ITEMS = [
  { title: 'In-app notifications', description: 'Click the bell icon in the top bar to see your notifications. Unread notifications show a badge count.' },
  { title: 'Task assignment alerts', description: 'You\'ll be notified when someone assigns a task to you or changes a task\'s status.' },
  { title: 'Email notifications', description: 'Configure email notifications in Settings > Notifications. Requires Microsoft Graph integration.' },
  { title: 'Reminders', description: 'Set reminders on tasks via HAL 9000 ("Remind me to..."). You\'ll get a notification at the scheduled time.' },
]

const FAQ_ITEMS = [
  {
    question: 'How do I invite team members?',
    answer: 'Admins and Managers can invite users from the Admin page. Click \'Invite User\' and search for people in your Azure Entra directory. They\'ll be added to the organization and can be assigned tasks immediately.',
  },
  {
    question: 'Can I customize the board columns?',
    answer: 'Yes! Click the board settings icon to add, rename, reorder, or delete columns to match your workflow.',
  },
  {
    question: 'How does HAL 9000 work?',
    answer: 'HAL 9000 (powered by Claude) can help you create tasks, search information, get summaries, set reminders, and more. It has access to your projects and tasks. Just type naturally in the HAL 9000 Chat page. It\'s very good at its job. Unsettlingly so.',
  },
  {
    question: 'Where can I see all my tasks?',
    answer: 'Click \'My Tasks\' in the sidebar to see all tasks assigned to you across all projects, with filtering and sorting options.',
  },
  {
    question: "What's the difference between Manager and Admin?",
    answer: 'Managers can create/edit/delete departments and invite users. Admins get everything Managers have plus user role management, organization settings, department assignments, and the ability to sync all users from Azure Entra.',
  },
  {
    question: 'How do I export my calendar?',
    answer: 'Go to Settings > Calendar to download .ics files for your tasks or reminders. Import them into Outlook, Google Calendar, or Apple Calendar.',
  },
  {
    question: 'How do departments work?',
    answer: 'Departments organize users and projects. Users are auto-linked to departments based on their Azure Entra profile. Admins can also manually assign users to departments.',
  },
  {
    question: 'Can I upload meeting transcripts?',
    answer: 'Yes! Go to the Transcripts page and upload a meeting transcript file. The AI will analyze it, extract action items, and can create tasks automatically in a new or existing project.',
  },
  {
    question: 'How do task dependencies work?',
    answer: "Open any task and scroll to the Dependencies section. You can mark a task as 'blocked by' another task. Blocked tasks show a visual indicator on the board.",
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes. The app uses Microsoft Entra ID for authentication, all API requests are authenticated, and data is scoped to your organization. No data is shared across organizations.',
  },
]

const PERMISSIONS_TABLE = [
  { action: 'View assigned tasks', admin: true, manager: true, member: true, viewer: true },
  { action: 'View project boards', admin: true, manager: true, member: true, viewer: true },
  { action: 'Create & edit tasks', admin: true, manager: true, member: true, viewer: false },
  { action: 'Comment on tasks', admin: true, manager: true, member: true, viewer: false },
  { action: 'Create projects', admin: true, manager: true, member: true, viewer: false },
  { action: 'Use HAL 9000', admin: true, manager: true, member: true, viewer: false },
  { action: 'Create departments', admin: true, manager: true, member: false, viewer: false },
  { action: 'Update departments', admin: true, manager: true, member: false, viewer: false },
  { action: 'Delete departments', admin: true, manager: true, member: false, viewer: false },
  { action: 'Invite users from Entra', admin: true, manager: true, member: false, viewer: false },
  { action: 'Assign user roles', admin: true, manager: false, member: false, viewer: false },
  { action: 'Assign users to departments', admin: true, manager: false, member: false, viewer: false },
  { action: 'Manage organization settings', admin: true, manager: false, member: false, viewer: false },
  { action: 'Sync all users from Entra', admin: true, manager: false, member: false, viewer: false },
  { action: 'View all org projects', admin: true, manager: false, member: false, viewer: false },
]

const ROLE_DESCRIPTIONS = [
  { role: 'Admin', description: 'Full access. Can manage users, roles, departments, organization settings, and sync from Azure Entra ID.' },
  { role: 'Manager', description: 'Everything a Member can do, plus create, update, and delete departments, and invite new users.' },
  { role: 'Member', description: 'Standard access. Can create and manage projects, tasks, boards, and use HAL 9000.' },
  { role: 'Viewer', description: 'Read-only access. Can view projects and tasks but cannot make changes.' },
]

function PermissionIcon({ allowed }) {
  if (allowed) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30">
        <HiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
      </span>
    )
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30">
      <HiX className="w-4 h-4 text-red-500 dark:text-red-400" />
    </span>
  )
}

function ItemList({ items }) {
  return (
    <div className="grid gap-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="p-3 rounded-lg bg-surface-50 dark:bg-surface-700 hover:bg-surface-100 dark:hover:bg-surface-600 transition-colors"
        >
          <p className="font-medium text-surface-800 dark:text-surface-200">{item.title}</p>
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">{item.description}</p>
        </div>
      ))}
    </div>
  )
}

function RolesPermissionsTab() {
  return (
    <div className="space-y-6">
      <p className="text-surface-600 dark:text-surface-400">
        Your organization has four roles: <strong className="text-surface-800 dark:text-surface-200">Admin</strong>,{' '}
        <strong className="text-surface-800 dark:text-surface-200">Manager</strong>,{' '}
        <strong className="text-surface-800 dark:text-surface-200">Member</strong>, and{' '}
        <strong className="text-surface-800 dark:text-surface-200">Viewer</strong>. Each role inherits access from the roles below it.
      </p>

      <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-100 dark:bg-surface-700">
              <th className="text-left px-4 py-3 font-semibold text-surface-700 dark:text-surface-200">Action</th>
              <th className="text-center px-3 py-3 font-semibold text-surface-700 dark:text-surface-200">Admin</th>
              <th className="text-center px-3 py-3 font-semibold text-surface-700 dark:text-surface-200">Manager</th>
              <th className="text-center px-3 py-3 font-semibold text-surface-700 dark:text-surface-200">Member</th>
              <th className="text-center px-3 py-3 font-semibold text-surface-700 dark:text-surface-200">Viewer</th>
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS_TABLE.map((row, idx) => (
              <tr
                key={idx}
                className={
                  idx % 2 === 0
                    ? 'bg-white dark:bg-surface-800'
                    : 'bg-surface-50 dark:bg-surface-900/50'
                }
              >
                <td className="px-4 py-2.5 text-surface-700 dark:text-surface-300">{row.action}</td>
                <td className="px-3 py-2.5 text-center"><PermissionIcon allowed={row.admin} /></td>
                <td className="px-3 py-2.5 text-center"><PermissionIcon allowed={row.manager} /></td>
                <td className="px-3 py-2.5 text-center"><PermissionIcon allowed={row.member} /></td>
                <td className="px-3 py-2.5 text-center"><PermissionIcon allowed={row.viewer} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-surface-800 dark:text-surface-200">Role Descriptions</h4>
        {ROLE_DESCRIPTIONS.map((item, idx) => (
          <div key={idx} className="p-3 rounded-lg bg-surface-50 dark:bg-surface-700">
            <p className="font-medium text-surface-800 dark:text-surface-200">{item.role}</p>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function FaqTab({ expandedFaq, setExpandedFaq }) {
  return (
    <div className="space-y-2">
      {FAQ_ITEMS.map((faq, idx) => (
        <div key={idx} className="border border-surface-200 dark:border-surface-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
          >
            <span className="font-medium text-surface-800 dark:text-surface-200">{faq.question}</span>
            <span className={`text-surface-400 dark:text-surface-500 transform transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          {expandedFaq === idx && (
            <div className="px-4 pb-4 text-surface-600 dark:text-surface-400">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default function HelpCenter({ isOpen, onClose, onRestartTour }) {
  const [activeTab, setActiveTab] = useState('getting-started')
  const [expandedFaq, setExpandedFaq] = useState(null)

  if (!isOpen) return null

  const renderContent = () => {
    switch (activeTab) {
      case 'getting-started':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiPlay className="w-5 h-5 text-brand-500" />
              Getting Started
            </h3>
            <ItemList items={GETTING_STARTED_ITEMS} />
          </div>
        )
      case 'tasks-boards':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiClipboardList className="w-5 h-5 text-brand-500" />
              Tasks &amp; Boards
            </h3>
            <ItemList items={TASKS_BOARDS_ITEMS} />
          </div>
        )
      case 'ai-assistant':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiChat className="w-5 h-5 text-brand-500" />
              HAL 9000 Tips
            </h3>
            <ItemList items={AI_ASSISTANT_ITEMS} />
          </div>
        )
      case 'roles-permissions':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiShieldCheck className="w-5 h-5 text-brand-500" />
              Roles &amp; Permissions
            </h3>
            <RolesPermissionsTab />
          </div>
        )
      case 'notifications':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiBell className="w-5 h-5 text-brand-500" />
              Notifications
            </h3>
            <ItemList items={NOTIFICATIONS_ITEMS} />
          </div>
        )
      case 'faq':
        return (
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-surface-800 dark:text-surface-200">
              <HiBookOpen className="w-5 h-5 text-brand-500" />
              Frequently Asked Questions
            </h3>
            <FaqTab expandedFaq={expandedFaq} setExpandedFaq={setExpandedFaq} />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col animate-fade-in border border-transparent dark:border-surface-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-100 dark:bg-brand-900/30">
              <HiQuestionMarkCircle className="w-5 h-5 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100">Help Center</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-surface-400 dark:text-surface-500 hover:text-surface-600 dark:hover:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-3 bg-gradient-to-r from-brand-50 to-purple-50 dark:from-brand-900/20 dark:to-purple-900/20 border-b border-surface-200 dark:border-surface-700">
          <h3 className="text-sm font-medium text-surface-600 dark:text-surface-400 mb-2">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            {onRestartTour && (
              <button
                onClick={onRestartTour}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-surface-700 text-sm font-medium text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors shadow-sm"
              >
                <HiPlay className="w-4 h-4 text-brand-500" />
                Restart Tour
              </button>
            )}
            <button
              onClick={() => setActiveTab('getting-started')}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-surface-700 text-sm font-medium text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors shadow-sm"
            >
              <HiPlay className="w-4 h-4 text-brand-500" />
              Getting Started
            </button>
            <button
              onClick={() => setActiveTab('ai-assistant')}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-surface-700 text-sm font-medium text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors shadow-sm"
            >
              <HiChat className="w-4 h-4 text-purple-500" />
              AI Tips
            </button>
            <a
              href="mailto:support@saltandstraw.com"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-surface-700 text-sm font-medium text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors shadow-sm"
            >
              <HiMail className="w-4 h-4 text-green-500" />
              Contact Support
            </a>
          </div>
        </div>

        {/* Body: Sidebar + Content */}
        <div className="flex flex-1 min-h-0">
          {/* Left Tab Sidebar */}
          <nav className="w-[180px] shrink-0 border-r border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900 py-2 overflow-y-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    setExpandedFaq(null)
                  }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors text-left ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border-r-2 border-brand-500'
                      : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-800 dark:hover:text-surface-200'
                  }`}
                >
                  <Icon className={`w-4.5 h-4.5 shrink-0 ${isActive ? 'text-brand-500 dark:text-brand-400' : 'text-surface-400 dark:text-surface-500'}`} />
                  <span className="truncate">{tab.label}</span>
                </button>
              )
            })}
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-surface-50 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-700">
          <p className="text-center text-sm text-surface-500 dark:text-surface-400">
            Need more help? Email us at{' '}
            <a href="mailto:support@saltandstraw.com" className="text-brand-600 dark:text-brand-400 hover:underline">
              support@saltandstraw.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
